'use client';
import { BottomBarCartEntityMobile } from '@entities/mobile/bottom-bar';
import { useCreateOrder } from '@hooks/queries/use-create-order';
import { useCart } from '@hooks/queries/use-query-cart';
import { getItemsForOrder } from '@utils/cart';
import { roundToHundredths } from '@utils/helpers';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import React, { useState } from 'react';

import { CardPaymentMethodEntity } from '@/entities';
import { useGetPaymentMethods } from '@/shared/api/wallet/generated/💰-оплата/💰-оплата';
import { WithCurrency } from '@/shared/hocs';
import { Portal } from '@/shared/portal';
import { Illustration } from '@/shared/ui';
import { AdditionalInfoMobile } from '@/shared/ui/mobile/additional-info';
import { BottomSheetUi } from '@/shared/ui/mobile/bottom-sheet';
import { useStore } from '@/store/context';

import styles from './bottom-bar.module.scss';
import { UserAgreement } from './user-agreement';

export const CartBottomBarMobile = observer(() => {
  const [sheetOpened, setSheetOpened] = useState(false);
  const { createOrder, isFetching } = useCreateOrder();
  const cart = useStore()?.cart;
  const auth = useStore().authorization;

  const { items, isLoading, summary } = useCart();
  const {
    data,
    isLoading: isPaymentsLoading,
    isSuccess,
  } = useGetPaymentMethods();

  const closeSheet = () => {
    setSheetOpened(false);
  };

  if (!items || isLoading) {
    return null;
  }

  const methods = data?.data;

  const handleCreateOrder = () => {
    cart?.validateTermsAndPayment();
    if (!auth.isAuth) {
      auth.openSignIn();
      return;
    }
    createOrder({
      products: getItemsForOrder(items),
      paymentMethod: cart?.checkoutInfo.activePaymentMethod,
      useBalance: cart?.useBalance,
      promoCode: cart?.checkoutInfo.promocodeInfo?.data.code || '',
    });
  };

  const selectedPaymentMethod = cart?.checkoutInfo?.activePaymentMethod;
  const infoVariant =
    summary && summary?.totalPrice >= Number(selectedPaymentMethod?.minAmount)
      ? 'info'
      : 'danger';

  return (
    <>
      <Portal selector={'#navbar-portal'}>
        <BottomBarCartEntityMobile
          leftSlot={
            <BottomBarCartEntityMobile.Info
              info={{
                totalAmount: roundToHundredths(
                  Number(summary?.totalPrice) ?? 0,
                ),
                quantity: summary?.count ?? 0,
              }}
            />
          }
          rightSlot={
            <BottomBarCartEntityMobile.Button
              onClick={() => {
                setSheetOpened(true);
                // cart?.updateItemsInCart();
              }}
              variant={'primary'}
              disabled={!summary?.count || summary.count > 10}
              width={'content'}
              as={motion.button}
              text={'Go to payment'}
              iconRight={
                <Illustration
                  id={'btn-icon'}
                  spriteName={'icons'}
                  name={'square-arrow-right'}
                />
              }
            />
          }
        />
      </Portal>

      <BottomSheetUi
        className={styles['sheet']}
        isOpen={sheetOpened}
        handleClose={closeSheet}
      >
        <BottomSheetUi.Content>
          <BottomSheetUi.Header>Payment method</BottomSheetUi.Header>

          <div className={styles['sheet-wrapper']}>
            <div className={styles['payment-methods']}>
              {Array.isArray(methods) &&
                isSuccess &&
                methods.length > 0 &&
                methods?.map((payment, idx) => {
                  return (
                    <CardPaymentMethodEntity
                      key={`${idx}-${payment?.id}-${payment?.currency}`}
                      toggleActive={() => cart?.setPaymentMethod(payment)}
                      isActive={selectedPaymentMethod?.id === payment?.id}
                      paymentMethod={payment}
                    />
                  );
                })}
            </div>
            <div className={styles['sheet-btns']}>
              {selectedPaymentMethod?.minAmount && (
                <AdditionalInfoMobile
                  variant={infoVariant}
                  icon={
                    <Illustration
                      name={'money-bag'}
                      spriteName={'icons'}
                      spanTagClassName={cn(
                        styles['min-amount-icon'],
                        styles[`min-amount-icon--${infoVariant}`],
                      )}
                    />
                  }
                >
                  Minimum amount:{' '}
                  <WithCurrency>
                    {selectedPaymentMethod?.minAmount}
                  </WithCurrency>
                </AdditionalInfoMobile>
              )}

              <AdditionalInfoMobile
                variant={'success'}
                icon={
                  <Illustration
                    style={{ color: 'green' }}
                    name={'tag-price'}
                    spriteName={'icons'}
                    spanTagClassName={styles.icon}
                  />
                }
              >
                Payments are protected by 3-D secure
              </AdditionalInfoMobile>

              <BottomSheetUi.Button
                text={'To pay'}
                disabled={infoVariant === 'danger'}
                iconLeft={
                  <Illustration
                    id={'btn-icon'}
                    name={'wallet-money-2'}
                    spriteName={'icons'}
                  />
                }
                onClick={handleCreateOrder}
              />

              <BottomSheetUi.Button
                variant={'secondary'}
                text={'Close'}
                onClick={closeSheet}
              />
            </div>

            <UserAgreement />
          </div>
        </BottomSheetUi.Content>
      </BottomSheetUi>
    </>
  );
});
